import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Scan, Settings, Globe, Camera, Loader2, AlertTriangle, CheckCircle2, XCircle, Heart, Mail, User, ChevronDown, Plus, Edit, Trash2, Crown, Clock, Menu, MessageCircle } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { trackProductScan, trackCameraUsage, trackAddFavorite, trackRemoveFavorite, trackAllergenConfig, trackProfileSave, trackProfileLoad, trackSuggestion, trackLanguageChange } from './analytics';
import { WelcomeModal } from '@/components/custom/WelcomeModal';
import { FavoritesModal } from '@/components/custom/FavoritesModal';
import { HistoryModal } from '@/components/custom/HistoryModal';
import { ProfilesModal } from '@/components/custom/ProfilesModal';
import { SuggestionsModal } from '@/components/custom/SuggestionsModal';
import { ProfileSelector } from '@/components/custom/ProfileSelector';
import { ProfileManager } from '@/components/custom/ProfileManager';
import { PaywallModal } from '@/components/custom/PaywallModal';
import { BetaModal } from '@/components/custom/BetaModal';
import { PricingPage } from '@/components/custom/PricingPage';
import { ContributePage } from '@/components/custom/ContributePage';
import { CustomAllergenSection } from '@/components/custom/CustomAllergenSection';
import { LandingPage } from '@/components/custom/LandingPage';
import { PrivacyPolicy } from '@/components/custom/PrivacyPolicy';
import { AdBanner } from '@/components/custom/AdBanner';
import { trackPageView, trackPWAInstall } from './analytics';
import api from './utils/api';
import { getRegion, setRegion as persistRegion, SUPPORTED_REGIONS } from './utils/regionManager';
import { getUserId } from './utils/userIdManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Scanner = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [barcode, setBarcode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [html5QrCode, setHtml5QrCode] = useState(null);
  const [hasConfig, setHasConfig] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [favoritesState, setFavoritesState] = useState({});
  
  // Profile and subscription state
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    is_beta_tester: false,
    active_subscriptions: ['free'],
    limits: { profiles: 1, suggestions: 1, favorites: false }
  });
  const [showProfileManager, setShowProfileManager] = useState(false);

  useEffect(() => {
    checkConfig();
    loadProfiles();
    loadSubscriptionStatus();
    // Auto-scan if barcode provided via URL (?barcode=XXX) — used by history re-scan
    const params = new URLSearchParams(window.location.search);
    const urlBarcode = params.get('barcode');
    if (urlBarcode) {
      setBarcode(urlBarcode);
      setTimeout(() => searchProduct(urlBarcode), 300);
      // Clean URL so reload doesn't re-trigger
      window.history.replaceState({}, '', '/scan');
    }
    return () => {
      if (html5QrCode) {
        html5QrCode.stop().catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    if (scanResult && scanResult.found) {
      checkIfFavorite(barcode);
    }
  }, [scanResult]);

  const checkConfig = async () => {
    try {
      const response = await api.get('/config/load');
      const allergens = response.data.allergens || [];
      const hasSelected = allergens.some(a => a.selected);
      setHasConfig(hasSelected);
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const loadProfiles = async () => {
    try {
      const response = await api.get('/profiles/list');
      const profilesList = response.data.profiles || [];
      setProfiles(profilesList);
      
      // Set active profile (default or first one)
      if (profilesList.length > 0) {
        const defaultProfile = profilesList.find(p => p.is_default) || profilesList[0];
        setActiveProfile(defaultProfile);
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const response = await api.get('/subscription/status');
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const checkIfFavorite = async (code) => {
    try {
      if (!activeProfile) return;
      const response = await api.get(`/favorites/check/${code}`, {
        params: { profile_id: activeProfile.id }
      });
      setIsFavorite(response.data.is_favorite);
    } catch (error) {
      console.error('Error checking favorite:', error);
    }
  };

  const toggleFavorite = async (product, code) => {
    try {
      if (isFavorite || favoritesState[code]) {
        await api.delete(`/favorites/remove/${code}`);
        setIsFavorite(false);
        setFavoritesState(prev => ({ ...prev, [code]: false }));
        toast.success(t('removedFromFavorites'));
        trackRemoveFavorite(product.product_name || product.name);
      } else {
        await api.post('/favorites/add', {
          barcode: code,
          product_name: product.product_name || product.name,
          brands: product.brands || '',
          image_url: product.image_url || ''
        });
        setIsFavorite(true);
        setFavoritesState(prev => ({ ...prev, [code]: true }));
        toast.success(t('addedToFavorites'));
        trackAddFavorite(product.product_name || product.name);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // 403 = free-tier limit reached (backend: "Has alcanzado el límite de 3 favoritos gratis...")
      if (error.response && error.response.status === 403) {
        const detail = error.response.data?.detail || '';
        toast.error(
          detail || 'Has alcanzado el límite de 3 favoritos gratuitos',
          {
            description: 'Activa Premium o un código Beta para guardar favoritos ilimitados.',
            duration: 7000,
            action: {
              label: 'Ver Premium',
              onClick: () => navigate('/pricing'),
            },
          }
        );
      } else {
        toast.error(t('errorSavingFavorite') || 'Error al guardar favorito');
      }
    }
  };

  const loadSuggestedProducts = async (code, forceGeneric = false) => {
    try {
      const endpoint = forceGeneric 
        ? '/products/suggestions/generic'
        : `/products/suggestions/${code}`;
      console.log('[SUGGESTIONS] Fetching from:', endpoint);
      const response = await api.get(endpoint);
      console.log('[SUGGESTIONS] Received:', response.data.suggestions?.length || 0, 'products');
      setSuggestedProducts(response.data.suggestions || []);
      
      // Check favorite status for each suggestion
      const newFavState = {};
      for (const product of response.data.suggestions || []) {
        try {
          const favResponse = await api.get(`/favorites/check/${product.barcode}`);
          newFavState[product.barcode] = favResponse.data.is_favorite;
        } catch (err) {
          newFavState[product.barcode] = false;
        }
      }
      setFavoritesState(newFavState);
      console.log('[SUGGESTIONS] State updated with', response.data.suggestions?.length || 0, 'products');
    } catch (error) {
      console.error('[SUGGESTIONS] Error loading suggestions:', error);
    }
  };

  const startScanning = async () => {
    // Live camera scanner (Yuka-style): opens rear camera and reads barcodes continuously.
    setIsScanning(true);

    // Wait for the reader DOM element to mount
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const qr = new Html5Qrcode('reader-live');
      setHtml5QrCode(qr);

      const onSuccess = async (decodedText) => {
        // Prevent multiple triggers
        try {
          await qr.stop();
          await qr.clear();
        } catch (e) {
          // ignore stop errors
        }
        setHtml5QrCode(null);
        setIsScanning(false);
        setBarcode(decodedText);
        toast.success('Código: ' + decodedText);
        searchProduct(decodedText);
      };

      const onFailure = () => {
        // Called on every frame that didn't decode — keep silent
      };

      await qr.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 150 },
          aspectRatio: 1.7778,
        },
        onSuccess,
        onFailure
      );
    } catch (err) {
      console.error('Camera error:', err);
      const msg = err?.message || String(err);
      if (msg.toLowerCase().includes('permission') || msg.toLowerCase().includes('notallowed')) {
        toast.error('Permiso de cámara denegado. Habilítala en los ajustes del navegador.');
      } else if (msg.toLowerCase().includes('notfound') || msg.toLowerCase().includes('no camera')) {
        toast.error('No se ha encontrado ninguna cámara en este dispositivo.');
      } else {
        toast.error('No se pudo iniciar la cámara. Intenta de nuevo.');
      }
      setIsScanning(false);
      setHtml5QrCode(null);
    }
  };

  const stopScanning = async () => {
    if (html5QrCode) {
      try {
        await html5QrCode.stop();
        await html5QrCode.clear();
      } catch (e) {
        // ignore
      }
      setHtml5QrCode(null);
    }
    setIsScanning(false);
  };

  const searchProduct = async (code) => {
    const searchCode = code || barcode;
    if (!searchCode.trim()) {
      toast.error('Por favor ingresa un código de barras');
      return;
    }

    setIsLoading(true);
    setScanResult(null);
    setSuggestedProducts([]);

    try {
      const response = await api.post('/product/scan', {
        barcode: searchCode,
        language: i18n.language
      });

      setScanResult(response.data);
      
      // Track product scan
      if (response.data.found) {
        trackProductScan(
          searchCode,
          response.data.product_name,
          response.data.safe_to_consume
        );
      }
      
      // Load suggestions for both found and not found products
      loadSuggestedProducts(searchCode);
    } catch (error) {
      console.error('Error scanning product:', error);
      toast.error('Error al escanear el producto');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-4" data-testid="scanner-page">
      <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
        <Card className="shadow-lg" data-testid="scanner-card">
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Scan className="w-5 h-5 sm:w-6 sm:h-6" />
              {t('scanProduct')}
            </CardTitle>
            <CardDescription className="text-sm">{t('enterBarcode')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            {!hasConfig && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4" data-testid="no-config-warning">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-900">{t('noAllergensConfigured')}</p>
                    <p className="text-sm text-amber-700 mt-1">{t('configureFirst')}</p>
                    <Button
                      data-testid="go-to-config-btn"
                      onClick={() => navigate('/config')}
                      variant="outline"
                      size="sm"
                      className="mt-2"
                    >
                      {t('goToConfig')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <Label htmlFor="barcode">{t('barcodePlaceholder')}</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    data-testid="barcode-input"
                    id="barcode"
                    type="text"
                    placeholder={t('barcodePlaceholder')}
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchProduct()}
                  />
                  <Button
                    data-testid="search-btn"
                    onClick={() => searchProduct()}
                    disabled={isLoading}
                    className="bg-[#8FBC8F] hover:bg-[#7CAA7C]"
                  >
                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('search')}
                  </Button>
                </div>
              </div>

              <div className="flex flex-col items-center gap-4">
                <div className="text-sm text-gray-500">o</div>

                {/* Live camera viewport — shown only while scanning */}
                <div
                  id="reader-live"
                  data-testid="camera-viewport"
                  className={
                    isScanning
                      ? 'w-full max-w-md mx-auto rounded-lg overflow-hidden border-2 border-[#8FBC8F] shadow-lg'
                      : 'hidden'
                  }
                  style={{ minHeight: isScanning ? '260px' : 0 }}
                />

                {isScanning ? (
                  <Button
                    data-testid="stop-camera-btn"
                    onClick={stopScanning}
                    variant="outline"
                    className="w-full"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    {t('cancel') || 'Cancelar'}
                  </Button>
                ) : (
                  <Button
                    data-testid="start-camera-btn"
                    onClick={startScanning}
                    variant="outline"
                    className="w-full"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {t('takePhoto') || 'Escanear con cámara'}
                  </Button>
                )}
              </div>

              {/* Hidden element kept for backward compatibility (file-based fallback) */}
              <div id="reader-hidden" style={{ display: 'none' }}></div>
            </div>
          </CardContent>
        </Card>

        {scanResult && (
          <>
            <Card
              data-testid="scan-result"
              className={`shadow-lg relative ${
                scanResult.found
                  ? scanResult.safe_to_consume
                    ? 'border-green-300 bg-green-50'
                    : 'border-red-300 bg-red-50'
                  : 'border-gray-300'
              }`}
            >
              {scanResult.found && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-4 right-4 z-10"
                  onClick={() => toggleFavorite(scanResult, barcode)}
                  data-testid="toggle-favorite-btn"
                >
                  <Heart
                    className={`w-6 h-6 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-400'}`}
                  />
                </Button>
              )}
              
              <CardHeader>
                {scanResult.found ? (
                  <>
                    <CardTitle className="flex items-center gap-2 pr-12">
                      {scanResult.safe_to_consume ? (
                        <CheckCircle2 data-testid="safe-icon" className="w-6 h-6 text-green-600" />
                      ) : (
                        <AlertTriangle data-testid="warning-icon" className="w-6 h-6 text-red-600" />
                      )}
                      {scanResult.product_name}
                    </CardTitle>
                    {scanResult.brands && (
                      <CardDescription>{t('brands')}: {scanResult.brands}</CardDescription>
                    )}
                    <div
                      data-testid="safety-status"
                      className={`mt-2 text-lg font-semibold ${
                        scanResult.safe_to_consume ? 'text-green-700' : 'text-red-700'
                      }`}
                    >
                      {scanResult.safe_to_consume ? t('safeToConsume') : t('containsAllergens')}
                    </div>
                  </>
                ) : (
                  <>
                    <CardTitle className="flex items-center gap-2">
                      <XCircle data-testid="not-found-icon" className="w-6 h-6 text-gray-600" />
                      {t('productNotFound')}
                    </CardTitle>
                    <CardDescription>{t('productNotFoundDesc')}</CardDescription>
                    {barcode && (
                      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                        <p className="text-amber-900">
                          <strong>{t('scannedCode', 'Código escaneado')}:</strong>{' '}
                          <code className="bg-white px-2 py-0.5 rounded font-mono text-xs">{barcode}</code>
                        </p>
                        <p className="text-amber-800 mt-2 text-xs leading-relaxed">
                          {t('notFoundHelp', 'Este producto no está en la base de datos de Open Food Facts. Verifica el código o, si es correcto, puedes contribuir añadiéndolo en')} {' '}
                          <a
                            href={`https://world.openfoodfacts.org/cgi/product.pl?type=add&code=${encodeURIComponent(barcode)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#8FBC8F] underline font-medium"
                            data-testid="contribute-off-link"
                          >
                            Open Food Facts
                          </a>.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setScanResult(null); setBarcode(''); }}
                          data-testid="try-another-btn"
                          className="mt-3"
                        >
                          <Scan className="w-4 h-4 mr-2" />
                          {t('tryAnother', 'Probar otro código')}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardHeader>
              {scanResult.found && (
                <CardContent className="space-y-4">
                  {scanResult.image_url && (
                    <img
                      data-testid="product-image"
                      src={scanResult.image_url}
                      alt={scanResult.product_name}
                      className="w-full max-w-xs mx-auto rounded-lg shadow"
                    />
                  )}

                  {scanResult.allergens_detected.length > 0 && (
                    <div data-testid="allergens-detected" className="space-y-3">
                      <h3 className="font-semibold text-lg text-red-700">{t('allergenDetected')}</h3>
                      {scanResult.allergens_detected.map((allergen, idx) => (
                        <div
                          key={idx}
                          data-testid={`allergen-${idx}`}
                          className="bg-white border-2 border-red-200 rounded-lg p-3"
                        >
                          <div className="font-semibold text-red-800">{allergen.allergen_name}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            <span className="font-medium">{t('foundIn')}:</span>
                            <ul className="list-disc list-inside mt-1">
                              {allergen.found_in.map((location, i) => (
                                <li key={i}>{location}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {scanResult.possible_allergens && scanResult.possible_allergens.length > 0 && (
                    <div data-testid="possible-allergens" className="space-y-3">
                      <h3 className="font-semibold text-lg text-orange-700 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        Posibles Alérgenos (aditivos ambiguos)
                      </h3>
                      {scanResult.possible_allergens.map((possible, idx) => (
                        <div
                          key={idx}
                          className="bg-white border-2 border-orange-200 rounded-lg p-3"
                        >
                          <div className="font-semibold text-orange-800">{possible.allergen_name}</div>
                          <div className="text-sm text-gray-600 mt-2">
                            <p className="font-medium mb-1">Aditivos detectados:</p>
                            {possible.additives.map((additive, i) => (
                              <div key={i} className="ml-2 mb-2">
                                <span className="font-semibold">{additive.additive}</span> - {additive.name}
                                <p className="text-xs text-gray-500 mt-1">⚠️ {additive.description}</p>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-orange-600 mt-2 italic">
                            No se puede confirmar el origen. Verifica la etiqueta o contacta al fabricante.
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {scanResult.ingredients_text && (
                    <div>
                      <h3 className="font-semibold text-lg mb-2">{t('ingredients')}</h3>
                      <p className="text-sm text-gray-700 bg-white p-3 rounded-lg border">
                        {scanResult.ingredients_text}
                      </p>
                    </div>
                  )}

                  {scanResult.additives_tags && scanResult.additives_tags.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-lg mb-2">{t('additives')}</h3>
                      <div className="flex flex-wrap gap-2">
                        {scanResult.additives_tags.map((additive, idx) => (
                          <span
                            key={idx}
                            className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                          >
                            {additive.replace('en:', '')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>

            {/* Suggested Products */}
            {suggestedProducts.length > 0 && (
              <Card className="shadow-lg border-2 border-[#8FBC8F]">
                <CardHeader>
                  <CardTitle className="text-[#8FBC8F] flex items-center gap-2">
                    <CheckCircle2 className="w-6 h-6" />
                    {t('suggestedProducts')}
                  </CardTitle>
                  <CardDescription>{t('safeFor')}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {suggestedProducts.map((product) => (
                      <Card key={product.barcode} className="relative border-2 border-green-200 bg-green-50">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 z-10"
                          onClick={() => toggleFavorite(product, product.barcode)}
                          data-testid={`fav-suggested-${product.barcode}`}
                        >
                          <Heart
                            className={`w-5 h-5 ${
                              favoritesState[product.barcode]
                                ? 'fill-red-500 text-red-500'
                                : 'text-gray-400'
                            }`}
                          />
                        </Button>
                        <CardContent className="p-4">
                          {product.image_url && (
                            <img
                              src={product.image_url}
                              alt={product.product_name}
                              className="w-full h-32 object-contain mb-2"
                            />
                          )}
                          <h3 className="font-semibold text-sm mb-2">{product.product_name}</h3>
                          {product.brands && (
                            <p className="text-xs text-gray-500 mb-2">{product.brands}</p>
                          )}
                          
                          {/* Show which allergens this product is FREE from */}
                          {product.free_from && product.free_from.length > 0 && (
                            <div className="mt-3 space-y-1">
                              <p className="text-xs font-semibold text-green-800 mb-1">
                                ✓ {t('noneOfYourAllergens')}
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {product.free_from.map((allergenId) => (
                                  <span
                                    key={allergenId}
                                    className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded border border-green-300 font-medium"
                                  >
                                    {t('freeFrom')} {t(allergenId)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  {/* Unlock more suggestions button */}
                  <div className="mt-6 text-center">
                    <Button
                      onClick={() => navigate('/pricing')}
                      variant="outline"
                      className="border-2 border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      {t('unlockMoreSuggestions') || 'Desbloquear más sugerencias'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Ad Banner for free users - shown after scan results */}
            <AdBanner className="mt-6" />
          </>
        )}
      </div>
    </div>
  );
};

const Config = () => {
  const { t } = useTranslation();
  const [allergensList, setAllergensList] = useState([]);
  const [selectedAllergens, setSelectedAllergens] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showProfilesModal, setShowProfilesModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [allergensResponse, configResponse] = await Promise.all([
        api.get('/allergens/list'),
        api.get('/config/load')
      ]);

      const allergens = allergensResponse.data.allergens || [];
      const userAllergens = configResponse.data.allergens || [];

      const mergedAllergens = allergens.map(allergen => {
        const userConfig = userAllergens.find(ua => ua.allergen_id === allergen.allergen_id);
        return userConfig || allergen;
      });

      setAllergensList(mergedAllergens);
      setSelectedAllergens(mergedAllergens.filter(a => a.selected));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAllergen = (allergenId) => {
    const allergen = allergensList.find(a => a.allergen_id === allergenId);
    if (!allergen) return;

    const isSelected = selectedAllergens.some(a => a.allergen_id === allergenId);

    if (isSelected) {
      setSelectedAllergens(selectedAllergens.filter(a => a.allergen_id !== allergenId));
    } else {
      setSelectedAllergens([...selectedAllergens, { ...allergen, selected: true }]);
    }
  };

  const saveConfig = async () => {
    setIsSaving(true);
    try {
      const configData = allergensList.map(allergen => ({
        ...allergen,
        selected: selectedAllergens.some(a => a.allergen_id === allergen.allergen_id)
      }));

      await api.post('/config/save', {
        allergens: configData,
        language: 'es'
      });

      toast.success(t('configSaved'));
      trackAllergenConfig(selectedAllergens.length);
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#8FBC8F]" />
      </div>
    );
  }

  const currentConfig = allergensList.map(allergen => ({
    ...allergen,
    selected: selectedAllergens.some(a => a.allergen_id === allergen.allergen_id)
  }));

  return (
    <div className="min-h-screen p-4" data-testid="config-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <CustomAllergenSection />
        
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-6 h-6" />
              {t('configureAllergens')}
            </CardTitle>
            <CardDescription>{t('selectAllergens')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Accordion type="multiple" className="w-full">
              {allergensList.map((allergen) => {
                const isSelected = selectedAllergens.some(a => a.allergen_id === allergen.allergen_id);
                return (
                  <AccordionItem key={allergen.allergen_id} value={allergen.allergen_id}>
                    <div className="flex items-center gap-3 py-4">
                      <Checkbox
                        data-testid={`checkbox-${allergen.allergen_id}`}
                        checked={isSelected}
                        onCheckedChange={() => toggleAllergen(allergen.allergen_id)}
                      />
                      <AccordionTrigger className="hover:no-underline flex-1 py-0">
                        <span className="font-medium">{t(allergen.allergen_id)}</span>
                      </AccordionTrigger>
                    </div>
                    <AccordionContent>
                      <div className="pl-8 space-y-2">
                        <p className="text-sm text-gray-600 mb-2">Incluye:</p>
                        <div className="flex flex-wrap gap-2">
                          {allergen.sub_items.map((subItem, idx) => (
                            <span
                              key={idx}
                              className={`text-xs px-2 py-1 rounded ${
                                subItem.code 
                                  ? 'bg-blue-100 text-blue-800 font-semibold' 
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {subItem.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>

            <div className="space-y-2">
              <Button
                data-testid="save-config-btn"
                onClick={saveConfig}
                disabled={isSaving}
                className="w-full bg-[#8FBC8F] hover:bg-[#7CAA7C]"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {t('saveConfiguration')}
              </Button>
              
              <Button
                data-testid="manage-profiles-btn"
                onClick={() => setShowProfilesModal(true)}
                variant="outline"
                className="w-full border-[#8FBC8F] text-[#8FBC8F] hover:bg-[#8FBC8F]/10"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('saveAsProfile') || 'Guardar como perfil'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <ProfilesModal
        isOpen={showProfilesModal}
        onClose={() => setShowProfilesModal(false)}
        currentAllergens={selectedAllergens}
        openWithForm={true}
      />
    </div>
  );
};

const Home = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentLang, setCurrentLang] = useState(i18n.language);
  const [currentRegion, setCurrentRegion] = useState(() => getRegion());
  const [showFavorites, setShowFavorites] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showProfiles, setShowProfiles] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentConfig, setCurrentConfig] = useState([]);
  const [showWelcomeAgain, setShowWelcomeAgain] = useState(false);
  
  // Profile and subscription management
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    is_beta_tester: false,
    active_subscriptions: ['free'],
    limits: { profiles: 1, suggestions: 1, favorites: false }
  });
  const [showProfileManager, setShowProfileManager] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState('profiles');
  const [showBetaModal, setShowBetaModal] = useState(false);

  useEffect(() => {
    loadCurrentConfig();
    loadProfiles();
    loadSubscriptionStatus();
    
    // Detectar instalación de PWA
    window.addEventListener('appinstalled', () => {
      console.log('PWA installed');
      trackPWAInstall();
    });
  }, []);

  // Track page views cuando cambia la ruta
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location]);

  const loadCurrentConfig = async () => {
    try {
      const response = await api.get('/config/load');
      setCurrentConfig(response.data.allergens || []);
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const loadProfiles = async () => {
    try {
      const response = await api.get('/profiles/list');
      const profilesList = response.data.profiles || [];
      setProfiles(profilesList);
      
      if (profilesList.length > 0) {
        const defaultProfile = profilesList.find(p => p.is_default) || profilesList[0];
        setActiveProfile(defaultProfile);
      } else {
        setActiveProfile(null);
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const response = await api.get('/subscription/status');
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const handleProfileChange = async (profile) => {
    try {
      console.log('Changing to profile:', profile.id, profile.profile_name);
      const response = await api.post(`/profiles/load/${profile.id}`);
      console.log('Profile load response:', response.data);
      setActiveProfile(profile);
      toast.success(`Perfil "${profile.profile_name}" cargado`);
      window.location.reload();
    } catch (error) {
      console.error('Error changing profile:', error);
      console.error('Error details:', error.response?.data);
      alert('Error al cambiar de perfil: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateProfile = async (profileName) => {
    try {
      // Include currently selected allergens when creating profile
      const selectedAllergens = currentConfig.filter(a => a.selected);
      
      await api.post('/profiles/create', {
        profile_name: profileName,
        allergens: selectedAllergens
      });
      await loadProfiles();
      setShowProfileManager(false);
    } catch (error) {
      if (error.response?.status === 403) {
        handleShowPaywall('profiles');
      } else {
        console.error('Error creating profile:', error);
        alert('Error al crear perfil');
      }
    }
  };

  const handleUpdateProfile = async (profileId, newName) => {
    try {
      await api.put(`/profiles/${profileId}`, {
        profile_name: newName
      });
      await loadProfiles();
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error al actualizar perfil');
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('¿Estás seguro de eliminar este perfil?')) return;
    
    try {
      console.log('Deleting profile from ProfileManager:', profileId);
      await api.delete(`/profiles/${profileId}`);
      console.log('Profile deleted successfully');
      await loadProfiles();
    } catch (error) {
      console.error('Error deleting profile:', error);
      console.error('Profile ID was:', profileId);
      console.error('Error response:', error.response?.data);
      alert('Error al eliminar perfil: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleShowPaywall = (feature) => {
    setPaywallFeature(feature);
    setShowPaywall(true);
    setShowProfileManager(false);
  };

  const handleBetaSuccess = (message) => {
    toast.success(message, {
      duration: 5000,
      icon: '👑'
    });
    loadSubscriptionStatus();
    setShowBetaModal(false);
  };

  const languages = [
    { code: 'es', name: 'Español' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'de', name: 'Deutsch' },
    { code: 'ca', name: 'Català' },
    { code: 'eu', name: 'Euskara' },
    { code: 'gl', name: 'Galego' },
    { code: 'it', name: 'Italiano' }
  ];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setCurrentLang(langCode);
    trackLanguageChange(langCode);
  };

  const changeRegion = (code) => {
    persistRegion(code);
    setCurrentRegion(code);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen">
      <WelcomeModal 
        forceOpen={showWelcomeAgain} 
        onForceClose={() => setShowWelcomeAgain(false)} 
      />
      
      {/* Profile Management Modal */}
      <ProfileManager
        open={showProfileManager}
        onOpenChange={setShowProfileManager}
        profiles={profiles}
        subscriptionLimits={subscriptionStatus.limits}
        onCreateProfile={handleCreateProfile}
        onUpdateProfile={handleUpdateProfile}
        onDeleteProfile={handleDeleteProfile}
        onShowPaywall={handleShowPaywall}
      />
      
      {/* Paywall Modal */}
      <PaywallModal
        open={showPaywall}
        onOpenChange={setShowPaywall}
        feature={paywallFeature}
      />
      
      {/* Beta Tester Modal */}
      <BetaModal
        open={showBetaModal}
        onOpenChange={setShowBetaModal}
        onSuccess={handleBetaSuccess}
      />
      
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4">
              <img src="/logo-transparent.png" alt="Logo" className="w-16 h-16 sm:w-20 sm:h-20" />
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ color: '#8FBC8F' }}>
                {t('appTitle')}
              </h1>
            </div>

            <div className="flex items-center gap-2">
              {activeProfile && (
                <ProfileSelector
                  profiles={profiles}
                  activeProfile={activeProfile}
                  onProfileChange={handleProfileChange}
                  onManageProfiles={() => setShowProfileManager(true)}
                  isBetaTester={subscriptionStatus.is_beta_tester}
                />
              )}

              {!subscriptionStatus.is_beta_tester && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBetaModal(true)}
                  className="text-xs"
                >
                  <Crown className="w-3 h-3 mr-1 text-yellow-500" />
                  Beta
                </Button>
              )}
            </div>
          </div>

          <nav className="flex gap-2 mt-4 items-center">
            <Button
              data-testid="nav-scan"
              variant={isActive('/') || isActive('/scan') ? 'default' : 'ghost'}
              onClick={() => {
                navigate('/scan');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className={`flex items-center gap-2 ${isActive('/') || isActive('/scan') ? 'bg-[#8FBC8F] hover:bg-[#7CAA7C]' : ''}`}
            >
              <Scan className="w-4 h-4" />
              {t('scanBarcode')}
            </Button>
            <Button
              data-testid="nav-config"
              variant={isActive('/config') ? 'default' : 'ghost'}
              onClick={() => {
                navigate('/config');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className={`flex items-center gap-2 ${isActive('/config') ? 'bg-[#8FBC8F] hover:bg-[#7CAA7C]' : ''}`}
            >
              <Settings className="w-4 h-4" />
              {t('myAllergens')}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="header-menu-btn"
                  title={t('menu') || 'Menú'}
                  aria-label={t('menu') || 'Menú'}
                  className="ml-auto h-10 w-10 p-0 border-2 border-[#8FBC8F]/30 hover:bg-[#8FBC8F]/10 hover:border-[#8FBC8F]"
                >
                  <Menu className="w-6 h-6 text-[#8FBC8F]" strokeWidth={2.5} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64" data-testid="header-menu-content">
                {/* Idioma */}
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  className="cursor-default focus:bg-accent text-base"
                  data-testid="menu-language-item"
                >
                  <span className="mr-2 text-lg" aria-hidden="true">🌐</span>
                  <span className="flex-1">{t('language') || 'Idioma'}</span>
                  <select
                    data-testid="language-selector"
                    value={currentLang}
                    onChange={(e) => changeLanguage(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="text-sm bg-transparent border border-gray-200 rounded px-2 py-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#8FBC8F]"
                  >
                    {languages.map((lang) => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                </DropdownMenuItem>

                {/* Regió */}
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  className="cursor-default focus:bg-accent text-base"
                  data-testid="menu-region-item"
                >
                  <span className="mr-2 text-lg" aria-hidden="true">📍</span>
                  <span className="flex-1">{t('region') || 'Región'}</span>
                  <select
                    data-testid="region-selector"
                    value={currentRegion}
                    onChange={(e) => changeRegion(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="text-sm bg-transparent border border-gray-200 rounded px-2 py-1 cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#8FBC8F]"
                  >
                    {SUPPORTED_REGIONS.map((r) => (
                      <option key={r.code || 'all'} value={r.code}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  onClick={() => setShowFavorites(true)}
                  data-testid="favorites-btn"
                  className="cursor-pointer text-base"
                >
                  <span className="mr-2 text-lg" aria-hidden="true">❤️</span>
                  {t('favorites')}
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() => setShowHistory(true)}
                  data-testid="history-btn"
                  className="cursor-pointer text-base"
                >
                  <span className="mr-2 text-lg" aria-hidden="true">🕒</span>
                  {t('scanHistory', 'Historial')}
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() => setShowProfiles(true)}
                  data-testid="profiles-btn"
                  className="cursor-pointer text-base"
                >
                  <span className="mr-2 text-lg" aria-hidden="true">👤</span>
                  {t('profiles')}
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() => setShowSuggestions(true)}
                  data-testid="suggestions-btn"
                  className="cursor-pointer text-base"
                >
                  <span className="mr-2 text-lg" aria-hidden="true">✉️</span>
                  {t('suggestions')}
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  onClick={() => setShowWelcomeAgain(true)}
                  data-testid="show-welcome-btn"
                  className="cursor-pointer text-base"
                >
                  <span className="mr-2 text-lg" aria-hidden="true">💬</span>
                  {t('showWelcome') || 'Ver mensaje de bienvenida'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>
      </header>

      <main className="w-full px-3 sm:px-4">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/scan" element={<Scanner />} />
          <Route path="/config" element={<Config />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/contribute" element={<ContributePage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Routes>
      </main>

      {/* Footer with Privacy Policy Link */}
      <footer className="bg-gray-100 border-t mt-8 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-600">
            <span>© {new Date().getFullYear()} {t('appTitle')}</span>
            <span className="hidden sm:inline">•</span>
            <button 
              onClick={() => navigate('/privacy')}
              className="text-[#8FBC8F] hover:underline"
              data-testid="footer-privacy-link"
            >
              {t('privacyPolicy')}
            </button>
            <span className="hidden sm:inline">•</span>
            <a 
              href="mailto:allergenapp.mail@gmail.com"
              className="text-[#8FBC8F] hover:underline"
            >
              allergenapp.mail@gmail.com
            </a>
          </div>
        </div>
      </footer>

      <FavoritesModal isOpen={showFavorites} onClose={() => setShowFavorites(false)} />
      <HistoryModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        onRescan={(bc) => navigate(`/scan?barcode=${encodeURIComponent(bc)}`)}
      />
      <ProfilesModal
        isOpen={showProfiles}
        onClose={() => setShowProfiles(false)}
        currentAllergens={currentConfig}
      />
      <SuggestionsModal isOpen={showSuggestions} onClose={() => setShowSuggestions(false)} />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Home />
      </BrowserRouter>
    </div>
  );
}

export default App;